import { HARDWARE_UPDATE_MESSAGES } from '@/constants';
import { useCsoPlaybackStore } from '@/stores/csoPlayback';
import { useDesktopAgentStore } from '@/stores/desktopAgentStore';
import { useShowOperatorStore } from '@/stores/showOperatorStore';
import { toast } from 'vue-sonner';

const desktop_agent_store = () => {
	return useDesktopAgentStore();
};

const show_operator_store = () => {
	return useShowOperatorStore();
};

const cso_playback_store = () => {
	return useCsoPlaybackStore();
};

const DESKTOP_AGENT_URL = 'ws://localhost:12345/websockets';
const HEARTBEAT_TIMEOUT_INTERVAL = 1000 * 10 + 1000 * 1; // 10 seconds + 1 second; Needs to be longer than servers heartbeat interval
let websocket = undefined;
let heartbeat_ping_timeout = undefined;

const MAX_RECONNECT_ATTEMPTS = 100;
const BASE_RECONNECT_DELAY = 2000; //2 second
const MAX_RECONNECT_DELAY = 1000 * 60; //1 minute

export const initDesktopAgentConnection = async () => {
	connectToDesktopAgent();
};

export const connectToDesktopAgent = () => {
	desktop_agent_store().is_reconnecting = false;
	desktop_agent_store().next_reconnect_attempt = undefined;

	websocket = new WebSocket(DESKTOP_AGENT_URL);

	websocket.addEventListener('open', async () => {
		console.log('[Desktop Agent] Connection established');
		desktop_agent_store().websocket_connection_status = 'CONNECTED';

		desktop_agent_store().reconnect_attempts = 0;
		if (desktop_agent_store().reconnection_timeout != undefined) {
			clearTimeout(desktop_agent_store().reconnection_timeout);
		}

		toast(`COBRA Agent application connected.`);

		show_operator_store().refreshAllData();
	});

	websocket.addEventListener('close', () => {
		console.log('[Desktop Agent] Connection closed', desktop_agent_store().is_reconnecting, desktop_agent_store().reconnect_attempts);

		if (desktop_agent_store().websocket_connection_status == 'CONNECTED') {
			//Only show the toast if the connection was disconnected after being connected
			toast(`COBRA Agent application disconnected.`);
		}

		desktop_agent_store().websocket_connection = undefined;
		desktop_agent_store().websocket_connection_status = 'DISCONNECTED';

		clearTimeout(heartbeat_ping_timeout);
		handleReconnecting();

		show_operator_store().refreshAllData();
	});

	websocket.addEventListener('message', async (event) => {
		// console.log('[Desktop Agent] Message from server ', event.data);

		try {
			try {
				var [type, payload] = JSON.parse(event.data);
			} catch (error) {
				console.log('Error parsing message', error?.message);
				throw new Error('Invalid message format');
			}

			// console.log('[Desktop Agent] Received message', type, payload, type == HARDWARE_UPDATE_MESSAGES.MODULES_UPDATED);

			if (type == HARDWARE_UPDATE_MESSAGES.MODULES_UPDATED) {
				show_operator_store().refreshModulesData();
			}

			// if (type == HARDWARE_UPDATE_MESSAGES.DEVICE_INFO_UPDATED) {
			// 	show_operator_store().refreshAllData();
			// }

			if (type == HARDWARE_UPDATE_MESSAGES.DEVICE_STATUS_UPDATED) {
				if (cso_playback_store().script_playback_status == 'PLAYING') {
					//Only refresh controller data while script is playing to get number of alternate events fired count.
					show_operator_store().refreshControllerData();
				} else {
					show_operator_store().refreshAllData();
				}
			}

			if (type == HARDWARE_UPDATE_MESSAGES.CONTROLLER_DISCONNECTED) {
				show_operator_store().refreshAllData();
			}

			if (type == HARDWARE_UPDATE_MESSAGES.ONE_MODULE_UPDATED) {
				show_operator_store().refreshSingleModuleData({ module_id: payload?.module?.parent_module_id ?? payload?.module?.module_id });
			}

			if (type == HARDWARE_UPDATE_MESSAGES.CURRENT_SESSION_SWITCHED) {
				show_operator_store().resetEnvironment();
			}

			if (type == 'event:scripts.updated') {
				await show_operator_store().refreshScriptData();
				await show_operator_store().getEventsList();
			}

			if (type == 'event:controller.script_playback_started') {
				const store = useCsoPlaybackStore();
				store.script_playback_elapsed_time = 0;
				store.script_playback_sync_at = typeof performance !== 'undefined' ? performance.now() : Date.now();
				store.show_script_started_at = Date.now();
				store.current_event_index = payload.last_script_ping?.event_index;
				store.script_playback_status = 'PLAYING';
			}

			if (type == 'event:controller.script_playback_resumed') {
				const store = useCsoPlaybackStore();
				store.script_playback_elapsed_time = parseFloat(payload.last_script_ping.elapsed_time);
				store.script_playback_sync_at = typeof performance !== 'undefined' ? performance.now() : Date.now();
				store.show_script_started_at = undefined;
				store.current_event_index = payload.last_script_ping?.event_index;
				store.script_playback_status = 'PLAYING';
			}

			if (type == 'event:controller.script_playback_paused') {
				const store = useCsoPlaybackStore();
				store.script_playback_elapsed_time = parseFloat(payload.last_script_ping.elapsed_time);
				store.script_playback_sync_at = typeof performance !== 'undefined' ? performance.now() : Date.now();
				store.current_event_index = payload.last_script_ping?.event_index;
				store.script_playback_status = 'PAUSED';
			}

			if (type == 'event:controller.script_playback_stopped') {
				const store = useCsoPlaybackStore();
				store.script_playback_status = 'STOPPED';
				store.script_playback_elapsed_time = 0;
				store.script_playback_sync_at = undefined;
				store.show_script_started_at = undefined;
				store.current_event_index = undefined;
				useShowOperatorStore().initializeChannelUsage();
			}

			if (type == 'event:controller.script_playback_ping') {
				const store = useCsoPlaybackStore();
				const ping_event_index = payload.last_script_ping?.event_index;
				if (ping_event_index !== store.current_event_index) {
					store.current_event_index = ping_event_index;
				}

				if (payload.status == 'PLAYING') {
					const elapsed = parseFloat(payload.last_script_ping.elapsed_time, store.script_playback_elapsed_time);
					if (store.script_playback_status !== 'PLAYING') {
						store.script_playback_status = 'PLAYING';
					}
					store.script_playback_elapsed_time = elapsed;
					store.script_playback_sync_at = typeof performance !== 'undefined' ? performance.now() : Date.now();
				}
			}

			if (type == 'event:controller.force_refreshed_modules') {
				toast.info('Modules have been refreshed by the controller.');
			}

			if (type == 'heartbeat:ping') {
				handleHeartbeatPing({ ping_payload: payload });
				return;
			}

			if (type == 'event:timer.created') {
				show_operator_store().applyTimerUpdate(payload.timer);
			}

			if (type == 'event:timer.updated') {
				show_operator_store().applyTimerUpdate(payload.timer);
			}

			if (type == 'event:timer.deleted') {
				show_operator_store().deleteTimer(payload.timer_id);
			}

			// router.handle(type, payload, websocket_connection, websocket_app.websocket_server);
		} catch (error) {
			console.error(error);
			websocket.send(JSON.stringify(['error', error.message]));
		}
	});

	websocket.addEventListener('error', (event) => {
		console.log('[Desktop Agent] Error ', event);
	});

	desktop_agent_store().websocket_connection = websocket;
};

const handleReconnecting = () => {
	if (desktop_agent_store().is_reconnecting == true) {
		return;
	}

	desktop_agent_store().is_reconnecting = true;
	desktop_agent_store().reconnect_attempts++;

	if (desktop_agent_store().reconnect_attempts > MAX_RECONNECT_ATTEMPTS) {
		console.log('[Desktop Agent] Max reconnect attempts reached, giving up');
		return;
	}

	const delay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, desktop_agent_store().reconnect_attempts), MAX_RECONNECT_DELAY); // Compare exponential value of current delay to max delay
	desktop_agent_store().next_reconnect_attempt = new Date(new Date().getTime() + delay);
	console.log(`[Desktop Agent] Reconnecting in ${delay / 1000} seconds...`, desktop_agent_store().next_reconnect_attempt);

	desktop_agent_store().reconnection_timeout = setTimeout(() => {
		connectToDesktopAgent();
	}, delay);
};

export const sendWebsocketMessage = ({ type, payload }) => {
	if (websocket == undefined) {
		return;
	}

	const message = JSON.stringify([type, payload]);
	websocket.send(message);
};

const handleHeartbeatPing = ({ ping_payload }) => {
	// console.log('[Desktop Agent] Handle heartbeat ping', ping_payload);
	if (websocket == undefined) {
		return;
	}

	if (heartbeat_ping_timeout != undefined) {
		clearTimeout(heartbeat_ping_timeout);
	}

	heartbeat_ping_timeout = setTimeout(() => {
		//We have not gotten a ping from the server in the necessary timeout period, close websocket connection
		console.log('[Desktop Agent] Closing websocket connection due to heartbeat timeout');
		websocket.close();
	}, HEARTBEAT_TIMEOUT_INTERVAL);

	if (ping_payload != undefined) {
		sendWebsocketMessage({ type: 'heartbeat:pong', payload: ping_payload });
	}
};
